import { defaultLocale, locales, type Locale } from "@/lib/i18n-config";

const LOCALE_PREFIX = new RegExp(`^/(${locales.join("|")})(?:/|$)`);

/**
 * Reads the locale from a leading path segment, e.g. `/id/dashboard` -> `id`.
 * Returns null rather than a default so callers can fall through to the next
 * source instead of committing to one too early.
 */
export function getLocaleFromPath(path: string | null | undefined): Locale | null {
  if (!path) {
    return null;
  }

  const match = LOCALE_PREFIX.exec(path);
  const candidate = match?.[1];

  return locales.find((locale) => locale === candidate) ?? null;
}

/**
 * Parses an Accept-Language header down to a supported locale. Extracted from
 * the middleware so the locale-less `/auth/*` routes can reuse the same parser
 * instead of growing a second, subtly different one.
 */
export function getLocaleFromAcceptLanguage(
  acceptLanguage: string | null | undefined,
): Locale {
  if (!acceptLanguage) {
    return defaultLocale;
  }

  const languageTags = acceptLanguage
    .split(",")
    .map((part) => part.split(";")[0]?.trim());

  for (const tag of languageTags) {
    if (!tag) continue;
    const normalized = tag.toLowerCase();

    const directMatch = locales.find(
      (locale) => locale.toLowerCase() === normalized,
    );
    if (directMatch) {
      return directMatch;
    }

    const base = normalized.split("-")[0];
    const partialMatch = locales.find(
      (locale) => locale.split("-")[0].toLowerCase() === base,
    );
    if (partialMatch) {
      return partialMatch;
    }
  }

  return defaultLocale;
}

/**
 * Best-effort locale for a request that the middleware never localized.
 * `/auth/*` short-circuits before locale rewriting, so there is no locale
 * header or cookie to read; the referring page and Accept-Language are all
 * that is left.
 */
export function getRequestLocale(request: Request): Locale {
  // Only ever used to pick a redirect target, so a request shape without usable
  // headers falls back to the default locale rather than failing the login.
  const headers = request?.headers;
  if (typeof headers?.get !== "function") {
    return defaultLocale;
  }

  const referer = headers.get("referer");

  if (referer) {
    try {
      const fromReferer = getLocaleFromPath(new URL(referer).pathname);
      if (fromReferer) {
        return fromReferer;
      }
    } catch {
      // A malformed Referer is not worth failing a login over.
    }
  }

  return getLocaleFromAcceptLanguage(headers.get("accept-language"));
}
